import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './types';

// Define the location object type
export interface LocationObject {
  bp_location_owner_id: string;
  bp_location_name: string;
  bp_location_is_default: boolean;
  bp_location_is_system_generated?: boolean;
  bp_location_type: string;
  bp_location_auto_create_stock_expense?: boolean;
  _id?: string;
  bp_location_code?: string;
  /** Cloudinary URL of the logo printed at the top of receipts. */
  bp_location_logo?: string;
  bp_location_settings?: {
    bp_location_currency: string;
    bp_location_timezone?: string;
    bp_location_allow_price_edit_during_checkout?: boolean;
    bp_location_allow_partial_orders?: boolean;
    bp_location_allow_staff_order_assignment?: boolean;
    /** Daily and monthly Z-index emails to the owner; server sends unless false. */
    bp_location_allow_zindex_report_email?: boolean;
  };
}

// Define the location slice state type
export interface LocationSliceObject {
  locations: LocationObject[];
  currentLocation: LocationObject | null;
  isLoading: boolean;
  currentLocationId: string | null;
  error: string | null;
  isLocationSwitching: boolean;
  // All locations mode for backoffice aggregate queries
  isAllLocations: boolean;
  // Version counter to trigger refetches when location context changes
  locationVersion: number;
  // Monotonic generation for the currently-selected location. Bumps only
  // when `currentLocationId` transitions to a different value. Consumers
  // capture this before an await and drop the write if it no longer matches.
  // Never resets to 0 (monotonic) — logout increments, doesn't zero, so a
  // stale in-flight response from a prior session can't collide with a
  // reused id.
  locationEpoch: number;
}

const initialState: LocationSliceObject = {
  locations: [],
  currentLocation: null,
  currentLocationId: null,
  isLoading: false,
  error: null,
  isLocationSwitching: false,
  isAllLocations: false,
  locationVersion: 0,
  locationEpoch: 0,
};

export const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setLocations: (state, action: PayloadAction<LocationObject[]>) => {
      state.locations = action.payload;
      // Set default location if none is currently selected
      if (!state.currentLocation && action.payload.length > 0) {
        const defaultLocation = action.payload.find(
          loc => loc.bp_location_is_default,
        );
        state.currentLocation = defaultLocation || null;
      }
    },
    setCurrentLocation: (
      state,
      action: PayloadAction<LocationObject | null>,
    ) => {
      state.currentLocation = action.payload;
    },
    setCurrentLocationId: (state, action: PayloadAction<string | null>) => {
      const prev = String(state.currentLocationId || '');
      const next = String(action.payload || '');
      if (prev !== next) {
        // Persisted state from before this field existed rehydrates as
        // undefined; treat missing as 0 so `+= 1` yields a number, not NaN.
        state.locationEpoch = (state.locationEpoch || 0) + 1;
      }
      state.currentLocationId = action.payload;
    },
    setLocationLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLocationSwitching: (state, action: PayloadAction<boolean>) => {
      state.isLocationSwitching = action.payload;
    },
    setIsAllLocations: (state, action: PayloadAction<boolean>) => {
      state.isAllLocations = action.payload;
      state.locationVersion += 1; // Auto-increment to trigger refetches
    },
    incrementLocationVersion: state => {
      state.locationVersion += 1;
    },
    setLocationError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearLocationError: state => {
      state.error = null;
    },
    clearLocations: state => {
      state.locations = [];
      state.currentLocation = null;
      state.currentLocationId = null;
      state.error = null;
      state.isLoading = false;
      state.isLocationSwitching = false;
      state.isAllLocations = false;
      state.locationVersion = 0;
      // Monotonic: bump instead of zeroing so any in-flight response tagged
      // with the previous epoch is dropped even if a later session reuses
      // the same location id. Coerce undefined-from-rehydrate to 0.
      state.locationEpoch = (state.locationEpoch || 0) + 1;
    },
    updateLocationSetting: (
      state,
      action: PayloadAction<Partial<LocationObject['bp_location_settings']>>,
    ) => {
      const settingsUpdate = action.payload;

      // Update current location
      if (state.currentLocation) {
        const existingSettings = state.currentLocation.bp_location_settings;
        state.currentLocation.bp_location_settings = {
          bp_location_currency: existingSettings?.bp_location_currency || '',
          ...existingSettings,
          ...settingsUpdate,
        };
      }

      // Update in locations array
      const currentLocationId = state.currentLocation?._id;
      if (currentLocationId) {
        state.locations = state.locations.map(loc => {
          if (loc._id === currentLocationId) {
            const existingSettings = loc.bp_location_settings;
            return {
              ...loc,
              bp_location_settings: {
                bp_location_currency:
                  existingSettings?.bp_location_currency || '',
                ...existingSettings,
                ...settingsUpdate,
              },
            };
          }
          return loc;
        });
      }
    },
  },
});

// Basic memoized selectors
export const selectLocations = (state: RootState) => state.location.locations;

export const selectCurrentLocation = (state: RootState) =>
  state.location.currentLocation;

export const selectLocationLoading = (state: RootState) =>
  state.location.isLoading;

export const selectLocationSwitching = (state: RootState) =>
  state.location.isLocationSwitching;

export const selectLocationError = (state: RootState) => state.location.error;

export const selectIsAllLocations = (state: RootState) =>
  state.location.isAllLocations;

export const selectLocationVersion = (state: RootState) =>
  state.location.locationVersion;

export const selectLocationEpoch = (state: RootState) =>
  state.location.locationEpoch;

// Memoized current location with validation
export const selectValidCurrentLocation = createSelector(
  [selectCurrentLocation],
  currentLocation => {
    return currentLocation && currentLocation._id ? currentLocation : null;
  },
);

// Memoized default location
export const selectDefaultLocation = createSelector(
  [selectLocations],
  locations => {
    return locations.find(location => location.bp_location_is_default) || null;
  },
);

// Memoized locations by type
export const selectLocationsByType = createSelector(
  [selectLocations, (state: RootState, locationType: string) => locationType],
  (locations, locationType) => {
    if (!locationType) return locations;
    return locations.filter(
      location => location.bp_location_type === locationType,
    );
  },
);

// Memoized location names for dropdown
export const selectLocationNames = createSelector(
  [selectLocations],
  locations => {
    return locations.map(location => ({
      id: location._id || location.bp_location_owner_id,
      name: location.bp_location_name,
      isDefault: location.bp_location_is_default,
    }));
  },
);

export const {
  setLocations,
  setCurrentLocation,
  setLocationLoading,
  setCurrentLocationId,
  setLocationSwitching,
  setIsAllLocations,
  incrementLocationVersion,
  setLocationError,
  clearLocationError,
  clearLocations,
  updateLocationSetting,
} = locationSlice.actions;

export default locationSlice.reducer;
